// ═══════════════════════════════════════════════════════════════
// KOTCH - TYPE DEFINITIONS
// A strategic 3-lane card game with combat triangle mechanics
// ═══════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────
// CORE ENUMS & TYPES
// ─────────────────────────────────────────────────────────────────

export type GamePhase =
  | 'setup'              // Game configuration
  | 'battleStart'        // Beginning of a new battle
  | 'playerTransition'   // Hot-seat privacy screen
  | 'commitment'         // Attacker/Defender committing cards
  | 'reveal'             // Cards flipping face-up
  | 'resolution'         // Lane-by-lane resolution animation
  | 'factionAbilitySelect' // Hydaria: pick a card to discard from opponent's hand
  | 'roundEnd'           // Round summary
  | 'battleEnd'          // Battle summary
  | 'matchEnd';          // Match over, final results

export type CommitmentSubPhase =
  | 'attackerCommit'     // Attacker selects cards for lanes
  | 'defenderCommit';    // Defender sees lanes, commits cards

export type CardType = 'spearman' | 'archer' | 'rider' | 'knight' | 'spy';
export type CardCategory = 'basic' | 'special';

export type Lane = 'left' | 'center' | 'right';
export const LANES: Lane[] = ['left', 'center', 'right'];

export type PlayerId = 1 | 2;

export type LaneDuelOutcome =
  | 'player1_wins'
  | 'player2_wins'
  | 'draw'
  | 'unopposed_p1'
  | 'unopposed_p2'
  | 'empty';

export type AIPersonality = 'easy' | 'medium' | 'hard';

export type Faction = 'Migora' | 'Hydaria' | 'Motra' | 'Rigata' | 'Vera';

// ─────────────────────────────────────────────────────────────────
// LANE REWARD TYPES
// ─────────────────────────────────────────────────────────────────

export type LaneRewardType = 'reveal_card' | 'faction_ability' | 'discard_deck';

export interface LaneReward {
  lane: Lane;
  type: LaneRewardType;
  description: string;
  icon: string;
}

export const LANE_REWARDS: Record<Lane, LaneReward> = {
  left: {
    lane: 'left',
    type: 'reveal_card',
    description: 'Reveal a card in opponent\'s hand',
    icon: '👁',
  },
  center: {
    lane: 'center',
    type: 'faction_ability',
    description: 'Trigger your faction ability',
    icon: '⚔',
  },
  right: {
    lane: 'right',
    type: 'discard_deck',
    description: 'Discard top card of opponent\'s deck',
    icon: '🗑',
  },
};

export interface LaneRewardResult {
  lane: Lane;
  winnerId: PlayerId;
  rewardType: LaneRewardType;
  applied: boolean;
  details?: string;
}

// ─────────────────────────────────────────────────────────────────
// FACTION ABILITY TYPES
// ─────────────────────────────────────────────────────────────────

export type FactionAbilityEffect =
  | 'draw_card'             // Rigata, Vera: Draw a card from deck
  | 'return_flank_card'     // Migora: Return one of your flank cards to hand
  | 'discard_opponent_hand' // Hydaria: Discard a card from opponent's hand
  | 'persist_center_card';  // Motra: Center winner stays on board next round

export interface FactionAbility {
  id: string;
  name: string;
  description: string;
  effect: FactionAbilityEffect;
}

// ─────────────────────────────────────────────────────────────────
// CARD TYPES
// ─────────────────────────────────────────────────────────────────

export interface CardAbility {
  id: string;
  name: string;
  trigger: 'on_reveal' | 'on_win' | 'on_lose' | 'before_reveal' | 'passive';
  description: string;
  effect: string;
}

export interface CardDefinition {
  id: string;
  type: CardType;
  power: number;
  category: CardCategory;
  name?: string;              // For named/unique cards
  description?: string;
  faction?: Faction;          // Knight faction affiliation
  ability?: CardAbility;      // Special abilities (display only for now)
}

export interface CardInstance {
  instanceId: string;         // Unique instance ID for this card in play
  definitionId: string;       // Reference to CardDefinition
}

// ─────────────────────────────────────────────────────────────────
// PLAYER TYPES
// ─────────────────────────────────────────────────────────────────

export interface Player {
  id: PlayerId;
  name: string;
  color: string;
  isAI: boolean;
  aiPersonality: AIPersonality | null;
  faction: Faction | null;        // Player's chosen faction

  hand: CardInstance[];           // Current battle hand (max 10)
  matchDeck: CardInstance[];      // Remaining cards for match (47 -> 37 -> 27)
  discardPile: CardInstance[];    // Cards used this match
  revealedCards: string[];        // instanceIds of cards revealed to opponent
}

export interface PlayerSetup {
  name: string;
  isAI: boolean;
  aiPersonality?: AIPersonality;
  color?: string;
  faction?: Faction;              // Selected faction for deck building
}

// ─────────────────────────────────────────────────────────────────
// LANE & COMMITMENT TYPES
// ─────────────────────────────────────────────────────────────────

export interface LaneCommitment {
  left: CardInstance | null;
  center: CardInstance | null;
  right: CardInstance | null;
}

export function createEmptyCommitment(): LaneCommitment {
  return { left: null, center: null, right: null };
}

export interface LaneDuelResult {
  lane: Lane;
  player1Card: CardInstance | null;
  player2Card: CardInstance | null;
  outcome: LaneDuelOutcome;
  winningPlayerId: PlayerId | null;
  reason: string;
}

export interface RoundResult {
  roundNumber: number;
  attackerId: PlayerId;
  laneResults: LaneDuelResult[];
  player1LanesWon: number;
  player2LanesWon: number;
  draws: number;
  player1TotalPower: number;
  player2TotalPower: number;
  winnerId: PlayerId | null;
  winReason: string;
  laneRewards?: LaneRewardResult[];  // Lane rewards applied this round
}

// ─────────────────────────────────────────────────────────────────
// BATTLE & MATCH TYPES
// ─────────────────────────────────────────────────────────────────

export interface BattleState {
  battleNumber: number;          // 1, 2, or 3
  roundNumber: number;           // Current round in this battle
  roundsPlayed: RoundResult[];
  player1RoundWins: number;
  player2RoundWins: number;
  roundDraws: number;
  firstAttackerId: PlayerId;     // Who attacked first in round 1
  isComplete: boolean;
  winnerId: PlayerId | null;
  winReason: string;
}

export interface MatchState {
  player1BattleWins: number;
  player2BattleWins: number;
  battlesPlayed: number;
  battlesToWin: number;          // Default: 2 (best of 3)
  currentBattle: BattleState;
  battleHistory: BattleState[];
}

// ─────────────────────────────────────────────────────────────────
// GAME CONFIGURATION
// ─────────────────────────────────────────────────────────────────

export interface GameConfig {
  player1Setup: PlayerSetup;
  player2Setup: PlayerSetup;
  cardsPerBattle: number;        // Default: 10
  battlesToWin: number;          // Default: 2 (best of 3)
}

export const DEFAULT_CONFIG: GameConfig = {
  player1Setup: { name: 'Player 1', isAI: false },
  player2Setup: { name: 'Player 2', isAI: true, aiPersonality: 'medium' },
  cardsPerBattle: 10,
  battlesToWin: 2,
};

// ─────────────────────────────────────────────────────────────────
// GAME STATE
// ─────────────────────────────────────────────────────────────────

export interface GameState {
  phase: GamePhase;
  commitmentSubPhase: CommitmentSubPhase | null;
  config: GameConfig;

  players: Record<PlayerId, Player>;
  match: MatchState;

  // Current round state
  currentAttackerId: PlayerId;
  currentDefenderId: PlayerId;

  // Commitments (face-down until reveal)
  player1Commitment: LaneCommitment;
  player2Commitment: LaneCommitment;

  // Reveal and resolution state
  revealed: boolean;
  currentRoundResult: RoundResult | null;
  resolvingLaneIndex: number;    // 0, 1, 2 for animation

  // Motra faction ability: persistent card from center lane
  persistentCard: { card: CardInstance; playerId: PlayerId } | null;
  // Lane locked by persistent card (can't be uncommitted)
  lockedLane: { playerId: PlayerId; lane: Lane } | null;
  // Hydaria faction ability: pending discard selection
  pendingAbility: {
    type: 'discard_opponent_hand';
    winnerId: PlayerId;
    loserId: PlayerId;
    eligibleCardIds: string[];
  } | null;

  // UI State
  selectedCardInstanceId: string | null;
  hoveredLane: Lane | null;
  message: string | null;
  animating: boolean;

  // Transition state
  transitioningPlayer: PlayerId | null;

  // Match result
  winner: PlayerId | null;
}

// ─────────────────────────────────────────────────────────────────
// GAME ACTIONS
// ─────────────────────────────────────────────────────────────────

export type GameAction =
  // Setup
  | { type: 'START_GAME'; config: GameConfig }
  | { type: 'RESET_GAME' }

  // Battle/Round flow
  | { type: 'START_BATTLE' }
  | { type: 'START_ROUND' }
  | { type: 'CONFIRM_PLAYER_READY' }

  // Card selection & commitment
  | { type: 'SELECT_CARD'; cardInstanceId: string | null }
  | { type: 'HOVER_LANE'; lane: Lane | null }
  | { type: 'COMMIT_CARD_TO_LANE'; lane: Lane }
  | { type: 'UNCOMMIT_CARD_FROM_LANE'; lane: Lane }
  | { type: 'CONFIRM_COMMITMENT' }

  // Resolution
  | { type: 'REVEAL_CARDS' }
  | { type: 'RESOLVE_NEXT_LANE' }
  | { type: 'COMPLETE_ROUND_RESOLUTION' }
  | { type: 'CONFIRM_ROUND_END' }
  | { type: 'CONFIRM_BATTLE_END' }

  // Faction ability
  | { type: 'SELECT_DISCARD_TARGET'; cardInstanceId: string }

  // AI
  | { type: 'AI_MAKE_COMMITMENT'; commitment: LaneCommitment }

  // UI
  | { type: 'SET_MESSAGE'; message: string | null }
  | { type: 'SET_ANIMATING'; animating: boolean }

  // Multiplayer sync
  | { type: 'SET_STATE'; state: GameState };

// ─────────────────────────────────────────────────────────────────
// GAME ACTIONS INTERFACE (for hook)
// ─────────────────────────────────────────────────────────────────

export interface GameActions {
  startGame: (config: GameConfig) => void;
  resetGame: () => void;
  startBattle: () => void;
  startRound: () => void;
  confirmPlayerReady: () => void;
  selectCard: (cardInstanceId: string | null) => void;
  hoverLane: (lane: Lane | null) => void;
  commitCardToLane: (lane: Lane) => void;
  uncommitCardFromLane: (lane: Lane) => void;
  confirmCommitment: () => void;
  revealCards: () => void;
  resolveNextLane: () => void;
  completeRoundResolution: () => void;
  confirmRoundEnd: () => void;
  confirmBattleEnd: () => void;
  selectDiscardTarget: (cardInstanceId: string) => void;
  aiMakeCommitment: (commitment: LaneCommitment) => void;
  setMessage: (message: string | null) => void;
  setAnimating: (animating: boolean) => void;
  setState: (state: GameState) => void;
}

// ─────────────────────────────────────────────────────────────────
// GAME HELPERS INTERFACE (for hook)
// ─────────────────────────────────────────────────────────────────

export interface GameHelpers {
  getPlayer: (id: PlayerId) => Player | undefined;
  getCurrentPlayer: () => Player | undefined;
  getOpponent: (playerId: PlayerId) => Player | undefined;
  getCurrentAttacker: () => Player | undefined;
  getCurrentDefender: () => Player | undefined;
  isAttacker: (playerId: PlayerId) => boolean;
  isDefender: (playerId: PlayerId) => boolean;
  getCardDefinition: (definitionId: string) => CardDefinition | undefined;
  getCardFromInstance: (instance: CardInstance) => CardDefinition | undefined;
  getPlayerCommitment: (playerId: PlayerId) => LaneCommitment;
  canCommitToLane: (playerId: PlayerId, lane: Lane) => boolean;
  getCommittedLanes: (playerId: PlayerId) => Lane[];
  getCommitmentCount: (playerId: PlayerId) => number;
  mustCommitAtLeast: (playerId: PlayerId) => number;
  hasCardsInHand: (playerId: PlayerId) => boolean;
  isCurrentPlayerAI: () => boolean;
  isPlayerTurn: (playerId: PlayerId) => boolean;
  getMatchScore: () => { p1Battles: number; p2Battles: number };
  getBattleScore: () => { p1Rounds: number; p2Rounds: number };
  canConfirmCommitment: (playerId: PlayerId) => boolean;
}

// ─────────────────────────────────────────────────────────────────
// PLAYER COLORS
// ─────────────────────────────────────────────────────────────────

export const PLAYER_COLORS = {
  player1: '#1a1a1a', // Near black
  player2: '#f5f5f5', // Near white
};

// ─────────────────────────────────────────────────────────────────
// AI CONFIG TYPES
// ─────────────────────────────────────────────────────────────────

export interface AIConfig {
  smartPlayChance: number;       // Probability of making strategic choices
  usesCardCounting: boolean;     // Tracks what cards have been played
  usesLaneStrategy: boolean;     // Considers lane importance
  bluffChance: number;           // Probability of bluffing with special cards
  conserveCardsChance: number;   // Probability of holding back cards
}
